import React, { ReactElement, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSplit, SplitContext } from 'react-splitio';
import LoadingWrapper from 'shared/components/LoadingWrapper';
import { getUserProfile } from '../user/getters';
import { RawUser } from '../user/types';

/*

`ToggleFeature` is a smart React container that renders its children when the
feature matches a certain value (usually `on`). The ToggleFeature container
is connected to Redux which has all of the enabled featurse preloaded. There is
an optional `untoggledComponent` prop which will render if the match fails.

Matching happens with the javascript `String.match` function which also accepts
a regular expression.  Use at your own peril.

See the `ToggleFeatureView` Container to turn on/off elements at the React
Router level.

How to Use:
```
import { ToggleFeature } from 'trustar-2.0/containers/features';
import { flags as Flags, treatments as Treatments } from 'modules/features';
import LoadingWrapper from 'shared/components/LoadingWrapper/LoadingWrapper';
import { useSelector } from 'react-redux';

 <ToggleFeature feature={Flags.DASHBOARD_ENABLED} match={treatments.ON}>
    <NavLink
      name={'Dashboard'}
      to='/dashboard'
      selector='DashboardLink'
      icon={<Icon icon={IconLookup.DB} />}
    />
 </ToggleFeature>
```
*/

const getUserSplitMeta = ({guid, email, firstName, lastName, company}: RawUser) => {
    const {maxIngestEmailsCount: enclaveInboxLimit, trialCompany, guid: companyGuid, name: companyName} = company;

    return {
        guid,
        email,
        firstName,
        lastName,
        enclaveInboxLimit,
        trialCompany,
        companyGuid,
        companyName
    }
};


type ToggleFeatureProps = {
    feature: string;
    match: string;
    untoggledComponent?: ReactElement | string;
    loadingComponent?: ReactElement | string;
    children: any;
};

export const ToggleFeature = ({
    feature = '',
    match = '',
    untoggledComponent,
    loadingComponent =  <LoadingWrapper showLoader={true}></LoadingWrapper>,
    children,
}: ToggleFeatureProps) => {
    const { isReady, lastUpdate, client } = useContext(SplitContext);
    const currentUser = useSelector(getUserProfile);
    const currentUserGuid = currentUser ? currentUser.guid : 'No-User-Data--Investigate';

    const userMeta = useMemo(() => {
        if (currentUser && currentUser.guid) {
            return getUserSplitMeta(currentUser);
        }

        return {
            guid: currentUserGuid
        }
    
    }, [currentUserGuid])

    const [featureValue, config] = useSplit(feature, userMeta);

    if (!isReady) {
        return <>{loadingComponent}</>;
    } else if (match.match(featureValue)) {
        return <>{children}</>;
    } else {
        return <>{untoggledComponent}</>;
    }
};

export default ToggleFeature;
